import React, { Fragment, useEffect, useState } from 'react';
import { Box, Button, Card, Flex, Heading } from 'rebass/styled-components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import gql from 'graphql-tag';
import styled from 'styled-components';

import TextInput from '../components/TextInput';
import FormField from '../components/FormField';
import Header from '../components/Header';
import withAnalytics from '../lib/withAnalytics';
import LoadingScreen from '../components/LoadingScreen';
import FullLayout2 from '../components/FullLayout';
import { ApolloError, useMutation } from '@apollo/client';
import { CleanDom } from '../utils/domCleanup';
import { IOkPayload } from '../graphql/types/mutations/generics';
import { toast } from 'react-toastify';
import { OtpInner } from './login/OtpInner';

const schema = Yup.object().shape({
  email: Yup.string()
    .email('invalid email address')
    .required('Required'),
  password: Yup.string()
    .min(6, 'min. length: 6')
    .trim()
    .required(),
  passwordConfirm: Yup
    .string()
    .trim()
    .oneOf([Yup.ref('password'), undefined], 'Passwords must match'),
});

const TRIGGER_RESET = gql`
  mutation providerPasswordReset(
    $email: String!
    $resetToken: String!
    $password: String!
    $otp: String
  ) {
    providerPasswordReset(email: $email, resetToken: $resetToken, password: $password, otp: $otp) {
      ok
    }
  }
`;

interface ITriggerResetOutput {
  providerPasswordReset: IOkPayload
}

interface ITriggerResetInput {
  email: string,
  resetToken: string,
  password: string,
  otp?: string
}

const Container = styled(Box)`
  max-width: 1024px;
  width: 100%;
  margin: 0 auto !important;
`;

const CardContainer = styled(Flex)`
  max-width: 400px;
`;

const ResetPassword: React.FC<{ analytics: any }> = ({ analytics }) => {
  const location = useLocation()
  const { id } = useParams()
  const navigate = useNavigate()
  const [mustEnterOtp, setMustEnterOtp] = useState(false);
  const [storedData, setStoredData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [triggerMutate, { loading }] = useMutation<ITriggerResetOutput, ITriggerResetInput>(TRIGGER_RESET, {
    onCompleted: (data) => {
      if (data && data.providerPasswordReset && data.providerPasswordReset.ok) {
        navigate('/login')
      }
    }
  })
  useEffect(() => {
    analytics.page();
    CleanDom()
    if (!id) {
      navigate('/login')
    }
  }, [])

  const handleSubmit = async (values: any) => {
    setIsLoading(true);
    if (!id) return
    if (!values.otp) {
      setStoredData({ ...values });
    }
    try {
      await triggerMutate({
        variables: {
          email: values.email,
          password: values.password,
          resetToken: id,
          otp: values.otp
        }
      })
    } catch (error: any) {
      setIsLoading(false);

      if (!error.graphQLErrors) {
        return;
      }

      error.graphQLErrors.forEach((error: any) => {
        if (error.message.toLowerCase() === 'error: otp req') {
          setMustEnterOtp(true);
          return;
        }
        if (error.message.toLowerCase() === 'error: account locked') {
          navigate(
            '/account-locked', {
            state: { email: values.email },
          });
        } else {
          toast.error(error.message, {
            autoClose: 3000
          });
          // toastManager.add(error.message, {
          //   appearance: 'error',
          //   autoDismiss: true,
          // });
        }
      });
    }
  };

  if (isLoading) {
    return <LoadingScreen loading={true} />;
  }

  return (
    <FullLayout2 style={{ overflow: 'auto', zIndex: 10 }}>
      <Fragment>
        <Container>
          <Flex flex={1} flexDirection="column">
            <Header dark={false} childContainerStyle={{ marginTop: '-16px' }}>
              <Heading sx={{ marginLeft: '153px' }}>Providers</Heading>
            </Header>
          </Flex>
          <Flex flex={1} alignItems="center" justifyContent="center">
            <CardContainer flex={1} px={3}>
              <Card p={4}>
                <Flex flexDirection="column">
                  <Box mb={4}>
                    <Heading textAlign="center" mb={3}>
                      Reset Your Password
                    </Heading>
                  </Box>
                  <Fragment>
                    {!mustEnterOtp ? (
                      <Formik
                        initialValues={{
                          email: '',
                          password: '',
                          passwordConfirm: '',
                        }}
                        isInitialValid={() => {
                          try {
                            const aa = schema.validateSync({
                              email: '',
                              password: '',
                              passwordConfirm: '',
                            });

                            if (aa.errors && (aa.errors as any).length) {
                              return false;
                            }
                            return true;
                          } catch (err) {
                            return false;
                          }
                        }}
                        validationSchema={schema}
                        onSubmit={(values) => handleSubmit(values)}
                        render={({ touched, errors, isValid }) => {
                          return (
                            <>
                              <Form>
                                <FormField mb={3} error={touched.email && errors.email}>
                                  <TextInput placeholder="Your email address" type="email" name="email" id="input_email" />
                                </FormField>

                                <FormField mb={4} error={touched.password && errors.password}>
                                  <TextInput placeholder="Password" type="password" name="password" id="input_password" />
                                </FormField>

                                <FormField mb={4} error={touched.passwordConfirm && errors.passwordConfirm}>
                                  <TextInput placeholder="Confirm Password" type="password" name="passwordConfirm" id="input_passwordConfirm" />
                                </FormField>

                                <Flex alignItems="center" flexDirection="column">
                                  <Button disabled={!isValid || loading || isLoading} variant="primary" width={1 / 2} type="submit" id="btn_login">
                                    Change Password
                                  </Button>
                                </Flex>
                              </Form>
                            </>
                          );
                        }} />) : (
                      <OtpInner
                        mutate={(otp: any) => {
                          handleSubmit({ ...storedData, otp });
                        }}
                        sendByEmail={() => { }}
                        loading={!!isLoading || !!loading}
                      />
                    )}

                  </Fragment>
                </Flex>
              </Card>
            </CardContainer>
          </Flex>
        </Container>
      </Fragment>
    </FullLayout2>
  );
};

// export default withAnalytics(withToastManager(Login));
export default withAnalytics(ResetPassword);
